import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { RoadSign, ROAD_SIGN_CODES, getRoadSignLabel, getRoadSignCategory, getRoadSignCategoryMeta } from '../components/RoadSign';
import { OfficialBadge, OfficialLegalNotice } from '../components/OfficialBadge';
import { ArrowRight, Image, Target } from 'lucide-react';
import useSeo from '../hooks/useSeo';
import { buildBreadcrumb } from '../seo/breadcrumbs';
import AudienceContextBanner from '../components/AudienceContextBanner';

const GROUPS = [
  {
    title: 'Panneaux de DANGER',
    color: '#cf2031',
    desc: 'Triangles à bord rouge — sommet en haut. Implantation officielle : 150 m avant le danger hors agglomération, 50 m en agglomération (Article R411-25 + IISR). Une plaque additionnelle « M2 » peut indiquer une distance différente.',
    codes: ['A1a', 'A1b', 'A1c', 'A1d', 'A2a', 'A2b', 'A3', 'A4', 'A13a', 'A13b', 'A14', 'A21'],
  },
  {
    title: 'Dangers spécifiques MOTO',
    color: '#0891b2',
    desc: 'À moto, certains panneaux exigent une vigilance accrue : perte de visibilité (virages), perte de contrôle (vent, chute de pierres) et perte d\'adhérence (animaux, rails). Anticipation et adaptation de la trajectoire indispensables.',
    codes: ['A19', 'A24', 'A15a1', 'A15b', 'A15c', 'A7', 'A8', 'A23'],
    motoTip: true,
  },
  {
    title: 'Intersections & Priorité',
    color: '#facc15',
    desc: 'Réglementent qui passe en premier à un croisement. STOP et Cédez le passage = arrêt obligatoire.',
    codes: ['AB1', 'AB2', 'AB3a', 'AB4', 'AB6', 'AB25'],
  },
  {
    title: "Panneaux d'INTERDICTION",
    color: '#cf2031',
    desc: 'Disque à bord rouge sur fond blanc. Imposent une interdiction immédiate.',
    codes: ['B1', 'B2a', 'B2b', 'B3', 'B6a1'],
  },
  {
    title: 'Limitations de vitesse',
    color: '#cf2031',
    desc: "Disque rouge avec valeur en km/h. La limite s'applique jusqu'au prochain panneau ou à la fin d'agglomération.",
    codes: ['B14_30', 'B14_50', 'B14_70', 'B14_80', 'B14_90', 'B14_110', 'B14_130'],
  },
  {
    title: "Panneaux d'OBLIGATION",
    color: '#1d4ed8',
    desc: 'Disque bleu plein. Imposent une action ou une direction.',
    codes: ['B21a1', 'B22a'],
  },
  {
    title: "Panneaux d'INDICATION",
    color: '#1d4ed8',
    desc: 'Carré bleu. Donnent une information utile (sens unique, voie réservée, etc.).',
    codes: ['C12', 'C13a', 'C20a', 'C24a', 'C107', 'C115'],
  },
  {
    title: 'Feux tricolores',
    color: '#1f2937',
    desc: 'Sens identique pour tous les usagers : rouge = arrêt total · orange = arrêt sauf si trop proche · vert = passage.',
    codes: ['FEU_ROUGE', 'FEU_ORANGE', 'FEU_VERT'],
  },
];

export default function PanneauxGallery() {
  const navigate = useNavigate();
  const total = ROAD_SIGN_CODES.length;

  useSeo({
    title: `Panneaux Moto 2026 — Galerie (${total} pictos)`,
    description: `${total} panneaux du Code Moto 2026 : danger, intersection, prescription, indication. SVG officiels conformes à l'arrêté du 24 nov. 1967.`,
    keywords: 'panneaux moto, signalisation routière, panneaux ETM, panneaux danger, panneaux prescription, panneaux indication, code moto signalisation, AB25, B21-1, panneaux cyclistes, M12, sas vélo, panneaux pédagogiques',
    path: '/panneaux',
    jsonLd: buildBreadcrumb([['Panneaux ETM', '/panneaux']]),
  });

  return (
    <div className="space-y-8 sm:space-y-10" data-testid="panneaux-gallery-page">
      <section className="mc-card p-6 sm:p-10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ background: '#cf2031' }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary-soft))] px-3 py-1 text-xs font-bold text-[hsl(var(--primary-dark))] mb-3">
            <Image className="w-3.5 h-3.5" /> ANTISÈCHE VISUELLE
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            Les {total} panneaux essentiels<br />
            <span className="text-[hsl(var(--primary))]">à connaître par cœur.</span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl">
            Pictogrammes officiels conformes à l'arrêté du 24 novembre 1967 sur la signalisation routière.
            Survole un panneau pour voir son code et sa signification.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <OfficialBadge size="md" />
            <Button
              onClick={() => navigate('/revision')}
              variant="outline"
              size="sm"
              className="rounded-full ml-auto"
              data-testid="cta-revision-from-panels"
            >
              <Target className="w-3.5 h-3.5 mr-1.5" /> S'entraîner sur les questions
            </Button>
          </div>
        </div>
      </section>

      {/* Bandeau d'adaptation par audience (visible pour les 3 publics) */}
      <AudienceContextBanner
        pageHint="La galerie des panneaux sert à apprendre, à enseigner et à les citer en oral CCS."
        hideOnEmpty
        contexts={{
          eleve: {
            title: 'Mémoriser les panneaux pour le code de la route',
            bullets: [
              'Identifie la forme, la couleur, le sens — c\'est ce que tu retrouveras en examen.',
              'Concentre-toi sur les panneaux à risque moto : virages, gravillons, animaux.',
            ],
          },
          enseignant: {
            title: 'Antisèche visuelle à projeter en classe',
            bullets: [
              'Réutilise la galerie en mode projection pour expliquer une famille de panneaux.',
              'Insiste sur les pièges courants : confusion danger ↔ obligation, ABx ↔ AB1.',
            ],
            cta: 'Voir le Grand Quiz C2 (sanctions)',
            ctaTo: '/enseignant/grand-quiz-c2',
          },
          'futur-enseignant': {
            title: 'Citer les panneaux par leur code officiel face au jury',
            bullets: [
              'Maîtrise les codes officiels (A1a, AB1, B6a1, B14, etc.) et leur famille.',
              'Connais la règle d\'implantation : 150 m hors agglo / 50 m en agglo.',
            ],
            cta: 'Voir le référentiel CCS',
            ctaTo: '/enseignant/referentiel',
          },
        }}
      />

      {/* Règle officielle d'implantation — encart pédagogique mémorable */}
      <section data-testid="panneaux-distances-rule" data-reveal="" className="space-y-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">Règle générale d'implantation</div>
          <h2 className="text-xl sm:text-2xl font-black tracking-tight">Distance avant le danger</h2>
          <p className="text-sm text-foreground/70 mt-1">
            Distance entre le panneau de signalisation avancée (panneau de danger ou type AB1/AB2/AB3b/AB5)
            et l'obstacle qu'il annonce.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="mc-card mc-card-accent-exam p-4 sm:p-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#cf2031] mb-2">
              HORS AGGLOMÉRATION
            </div>
            <div className="text-3xl sm:text-4xl font-black text-foreground tracking-tight leading-none">
              150 m
            </div>
            <p className="text-xs text-foreground/70 mt-2 leading-snug">
              Distance standard sur routes, départementales et nationales.
              Vitesse de circulation supérieure à 50 km/h → besoin de plus de marge pour anticiper.
            </p>
          </div>
          <div className="mc-card mc-card-accent-training p-4 sm:p-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#1d4ed8] mb-2">
              EN AGGLOMÉRATION
            </div>
            <div className="text-3xl sm:text-4xl font-black text-foreground tracking-tight leading-none">
              0 à 50 m
            </div>
            <p className="text-xs text-foreground/70 mt-2 leading-snug">
              Variable selon l'écart entre les intersections.
              <strong className="text-foreground"> Panneaux AB1 / AB2 : 0 à 30 m.</strong>
              Si distance &lt; 50 m, arrondie aux 10 m près sur le panonceau.
            </p>
          </div>
          <div className="mc-card mc-card-accent-success p-4 sm:p-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#16a34a] mb-2">
              ROUTE RAPIDE / AUTOROUTE
            </div>
            <div className="text-3xl sm:text-4xl font-black text-foreground tracking-tight leading-none">
              jusqu'à 400 m
            </div>
            <p className="text-xs text-foreground/70 mt-2 leading-snug">
              Sur les routes à très bonne visibilité parcourues par une circulation rapide.
              Distance prolongée pour anticiper à grande vitesse.
            </p>
          </div>
        </div>
      </section>

      {/* Cas particuliers — exceptions à connaître */}
      <section data-testid="panneaux-cas-particuliers" data-reveal="" className="space-y-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">À retenir absolument</div>
          <h2 className="text-xl sm:text-2xl font-black tracking-tight">5 cas particuliers à mémoriser</h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="mc-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#cf2031]/15 text-[#cf2031] font-black text-xs">A18</span>
              <div className="font-black text-sm">Panneau « double sens »</div>
            </div>
            <p className="text-xs text-foreground/75 leading-snug">
              Implanté <strong>directement</strong> au début de la circulation à double sens —
              pas en amont. Vrai en agglo comme hors agglo.
            </p>
          </div>

          <div className="mc-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#cf2031]/15 text-[#cf2031] font-black text-xs">AB4</span>
              <div className="font-black text-sm">Panneau STOP en ville</div>
            </div>
            <p className="text-xs text-foreground/75 leading-snug">
              Si la vitesse est limitée à 50 km/h ou moins, le STOP peut être <strong>fixé directement
              à l'endroit où il faut s'arrêter</strong>, sans signal avancé obligatoire.
            </p>
          </div>

          <div className="mc-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-500/15 text-amber-500 font-black text-xs">200m</span>
              <div className="font-black text-sm">Panneau loin de l'intersection</div>
            </div>
            <p className="text-xs text-foreground/75 leading-snug">
              Si un AB3a ou AB4 est placé à <strong>plus de 200 m d'une intersection</strong>,
              il doit être <strong>répété à mi-distance</strong> avec un panonceau de distance
              sur les deux panneaux.
            </p>
          </div>

          <div className="mc-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-500/15 text-amber-500 font-black text-xs">100m</span>
              <div className="font-black text-sm">Distance plancher</div>
            </div>
            <p className="text-xs text-foreground/75 leading-snug">
              Une distance d'implantation <strong>inférieure à 100 m</strong> hors agglo n'est
              tolérée qu'en circonstance exceptionnelle (intersections très rapprochées,
              panneaux successifs).
            </p>
          </div>

          <div className="mc-card p-4 sm:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1d4ed8]/15 text-[#1d4ed8] font-black text-xs">⚡</span>
              <div className="font-black text-sm">Panneaux lumineux à message variable</div>
            </div>
            <p className="text-xs text-foreground/75 leading-snug">
              Hors règle : ils ne s'allument <strong>que lorsque le danger est présent</strong>
              (vent fort, brouillard, accident en aval…). Visibles très en amont (parfois
              plusieurs kilomètres) — pas concernés par les distances 50/150/400 m.
            </p>
          </div>
        </div>
      </section>

      {/* Panonceaux M1 / M2 — différence cruciale */}
      <section data-testid="panneaux-panonceaux" data-reveal="" className="space-y-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">À ne pas confondre</div>
          <h2 className="text-xl sm:text-2xl font-black tracking-tight">Panonceaux M1 vs M2</h2>
          <p className="text-sm text-foreground/70 mt-1">
            Le panonceau est la petite plaque <strong>placée sous</strong> un panneau qui complète
            son sens. Deux types s'opposent dans le sens.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="mc-card mc-card-accent-training p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-md bg-[#1d4ed8] text-white font-black text-sm font-mono">M1</span>
              <div className="font-black text-base">Panonceau de distance</div>
            </div>
            <div className="text-2xl font-black mb-1">
              <span className="font-mono">150 m</span>
            </div>
            <p className="text-xs text-foreground/75 leading-snug">
              Indique la <strong>distance à parcourir avant d'atteindre le danger</strong>.
              Une seule valeur, sans flèche.
            </p>
            <p className="text-[11px] text-foreground/55 italic mt-2">
              Exemple : un panneau A1a + M1 « 150 m » = virage à gauche dans 150 mètres.
            </p>
          </div>
          <div className="mc-card mc-card-accent-exam p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-md bg-[#cf2031] text-white font-black text-sm font-mono">M2</span>
              <div className="font-black text-base">Panonceau d'étendue</div>
            </div>
            <div className="text-2xl font-black mb-1">
              <span className="font-mono">← 3 km →</span>
            </div>
            <p className="text-xs text-foreground/75 leading-snug">
              Indique la <strong>longueur sur laquelle s'étend le danger</strong>.
              Reconnaissable aux <strong>flèches de chaque côté</strong> de la valeur.
            </p>
            <p className="text-[11px] text-foreground/55 italic mt-2">
              Exemple : un panneau A4 + M2 « ← 3 km → » = chaussée glissante sur 3 kilomètres.
            </p>
          </div>
        </div>
      </section>

      {/* Article R411-25 — feux prévalent sur panneaux */}
      <section data-testid="panneaux-feux-priorite" data-reveal="" className="space-y-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">Article R411-25 du Code de la route</div>
          <h2 className="text-xl sm:text-2xl font-black tracking-tight">À une intersection à feux : les feux prévalent</h2>
        </div>

        <div className="mc-card mc-card-accent-official p-4 sm:p-5">
          <p className="text-sm text-foreground/85 leading-relaxed">
            <em>« Les indications des feux de signalisation lumineux <strong>prévalent</strong>
            sur celles qui sont données par les signaux routiers réglementant la priorité. »</em>
          </p>
          <div className="text-xs text-foreground/65 italic mt-2">
            — Article R411-25 du Code de la route
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/70 mb-2">
              Conséquence pratique
            </div>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Quand des panneaux de priorité (AB1, AB2, AB3a, AB4…) sont placés à un carrefour
              équipé de feux tricolores, ils ne s'appliquent <strong>que dans 4 cas</strong> :
            </p>
            <ul className="text-sm text-foreground/80 leading-relaxed list-disc pl-5 mt-2 space-y-1">
              <li>Quand l'<strong>installation est éteinte</strong> (panne, hors service)</li>
              <li>Quand le carrefour fonctionne en <strong>jaune clignotant général</strong> (signaux du milieu uniquement)</li>
              <li>En permanence, sur une entrée à <strong>signal tricolore en jaune clignotant</strong> (feu du bas allumé)</li>
              <li>En permanence, sur une entrée <strong>non contrôlée par feux</strong> mais munie d'un AB3a ou AB4</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Référence légale */}
      <div className="mc-card mc-card-accent-official p-3 text-xs text-foreground/70 italic" data-testid="panneaux-distances-source">
        Sources officielles : Articles R411-25 et suivants du Code de la route + Instruction
        interministérielle sur la signalisation routière (IISR, livre 1, 1ʳᵉ partie). Le poteau
        d'un panneau est implanté à au moins 2 m du marquage de la bande de rive ; le bas du
        panonceau à 1 m du sol (rase campagne) ou à 2,30 m de hauteur en agglomération.
      </div>

      {GROUPS.map((g, gi) => (
        <section key={gi} className="space-y-4" data-testid={`group-${gi}`}>
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-block w-3 h-3 rounded-full" style={{ background: g.color }} />
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                  Catégorie {gi + 1}/{GROUPS.length}
                </div>
              </div>
              <h2 className="text-xl sm:text-2xl font-black tracking-tight">{g.title}</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{g.desc}</p>
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              {g.codes.length} panneaux
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {g.codes.map(code => {
              const label = getRoadSignLabel(code) || code;
              const cat = getRoadSignCategory(code);
              const meta = getRoadSignCategoryMeta(cat);
              return (
                <div
                  key={code}
                  data-testid={`gallery-sign-${code}`}
                  className="mc-card p-4 flex flex-col items-center gap-2.5 hover:-translate-y-0.5 hover:shadow-lg transition-all cursor-default"
                  title={`${code} — ${label}`}
                >
                  <RoadSign code={code} size={88} />
                  <div className="text-center w-full">
                    <div className="text-[10px] font-mono font-black text-foreground/70">{code}</div>
                    {meta && (
                      <div
                        className="inline-block mt-1 px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest"
                        style={{ background: meta.accentSoft, color: meta.accent }}
                      >
                        {meta.label}
                      </div>
                    )}
                    <div className="text-[11px] text-foreground/85 mt-1.5 leading-tight font-semibold">
                      {label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {g.motoTip && (
            <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="moto-risk-grid">
              <div className="rounded-2xl border-l-4 border-l-[#cf2031] bg-card p-4">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#cf2031] mb-1">Risque 1 — Visibilité</div>
                <div className="font-bold text-sm mb-1">Virages (A1a → A1d)</div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  La force centrifuge pousse la moto vers l'extérieur. Couper les gaz avant le virage,
                  incliner la moto, reprendre les gaz au point de pivot. Trajectoire de sécurité :
                  élargir à l'entrée pour gagner en visibilité.
                </p>
              </div>
              <div className="rounded-2xl border-l-4 border-l-[#b45309] bg-card p-4">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#b45309] mb-1">Risque 2 — Contrôle</div>
                <div className="font-bold text-sm mb-1">A2a/b · A19 · A24</div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Dos d'âne, ralentisseur, chute de pierres, vent latéral : ralentir, augmenter la
                  distance de sécurité, fermer la visière, se placer au centre de la voie pour avoir
                  une marge de manœuvre.
                </p>
              </div>
              <div className="rounded-2xl border-l-4 border-l-[#0891b2] bg-card p-4">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#0891b2] mb-1">Risque 3 — Adhérence</div>
                <div className="font-bold text-sm mb-1">A15a/b/c · A7 · A8 · A23</div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Animaux (déjections glissantes), rails, marquages au sol : effet de surprise +
                  perte d'adhérence par temps de pluie. Éviter de freiner sur les bandes blanches
                  ou les rails, traverser perpendiculairement.
                </p>
              </div>
            </div>
          )}
        </section>
      ))}

      <section className="mc-card p-5 sm:p-6 border-l-4 border-l-[hsl(var(--primary))]" data-testid="study-tips">
        <h3 className="font-black text-lg mb-3">Méthode pour mémoriser les panneaux</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { t: 'Apprends par forme', d: 'Triangle = danger · Rond rouge = interdit · Rond bleu = obligation · Carré bleu = info.' },
            { t: 'Apprends par couleur', d: 'Rouge = arrêt/interdit · Bleu = obligation/info · Jaune (losange) = priorité.' },
            { t: 'Pratique avec un dossier ETM', d: 'Lis le dossier thème R (Route) — beaucoup de panneaux illustrés et expliqués en contexte.' },
            { t: "Fixe l'image au sens", d: 'Crée une histoire pour chaque panneau (ex: A13a = "des enfants jouent ici").' },
          ].map((x, i) => (
            <div key={i} className="flex items-start gap-2.5 rounded-2xl border border-border bg-card p-3.5">
              <span className="mc-dot mt-0.5">{i + 1}</span>
              <div>
                <div className="font-bold text-sm">{x.t}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{x.d}</div>
              </div>
            </div>
          ))}
        </div>
        <Button
          onClick={() => navigate('/revision')}
          className="mc-btn-primary mt-5"
          data-testid="cta-final-revision"
        >
          M'entraîner sur les panneaux <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </section>

      <OfficialLegalNotice />
    </div>
  );
}
